import {
  blockMetaInputSchema,
  dateTypeSchema,
  objectIdSchema,
  Org,
  OrgSchema,
  orgStatusSchema,
  paginationWithSearchSchema,
  partyRoleSchema,
} from '@hero/schema';
import { z } from 'zod';
import { API } from '..';

const schema = OrgSchema.model;

/**
 * `Org` collection — the unified, single source of truth for every
 * organizational entity that used to live in `Business` or `Carrier`.
 * Roles drive the org's network capabilities (`carrier_company`,
 * `business_customer`, `broker`, …); the same Org doc may carry many.
 *
 * Backfill migrations (`apps/backend/migrations/*relations-v2*`) seed
 * `Org` from the legacy collections, preserving `Org._id == legacy._id`
 * so existing foreign keys keep resolving.
 *
 * See `docs/relations-v2/07-collection-unification.md`.
 */

//* read
export const orgGetAllAPISchema = paginationWithSearchSchema.extend({
  /** Optional: filter by role. */
  role: partyRoleSchema.optional(),
  /** Optional: filter by status. */
  status: schema.shape.status.optional(),
  /**
   * Window filter on `meta.lastSeenOnline.at` (`today` / `yesterday` /
   * `this_week` / …). Same `dateTypeSchema` + `mapSimpleDateFilter`
   * path used by orders / users.
   */
  lastSeenOnlineDate: dateTypeSchema.optional(),
});
export type OrgGetAllAPI = API<z.infer<typeof orgGetAllAPISchema>, Org[]>;

export const orgGetOneAPISchema = schema.pick({ _id: true });
export type OrgGetOneAPI = API<z.infer<typeof orgGetOneAPISchema>, Org>;

/** Super-admin: block / activate an org with an audit trail. */
export const orgUpdateAPISchema = z.object({
  _id: objectIdSchema,
  status: orgStatusSchema.optional(),
  /** Required when `status === 'blocked'`. Cleared server-side on activate. */
  blockMeta: blockMetaInputSchema.optional(),
});
export type OrgUpdateAPI = API<z.infer<typeof orgUpdateAPISchema>, Org>;
